import {
  ElementRef,
  HostListener,
  Input,
  AfterViewInit,
  OnDestroy,
  Directive,
  InjectionToken,
  Inject,
  Injector,
  Type,
} from "@angular/core";
import {
  BfxBaseBlotterContextMenuService,
  BfxLadderBlotterContextMenuEvent,
  BfxLadderBlotterContextMenuEventType,
  DELETED_SELECTED_ITEM,
} from "./bfx-base-blotter-contextmenu.service";
import { AnimationEvent } from "@angular/animations";
import {
  BfxBlotterAction,
  BfxBlotterContextMenuItem,
  PrimaryBlotterLookupService,
} from "shared/cubiq-services";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { Subject, Subscription, auditTime, filter } from "rxjs";
import { PointTopLeft } from "openfin/_v2/api/system/point";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { ResizeObserverScheduler } from "shared/helper-services/resize-observer-scheduler";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";
import { ConcreteEncodable } from "../../shared/cubiq-model-multi-asset";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";
import { BfxBaseBlotter } from "./bfx-base-blotter";

type RefEntry = {
  window: NativeDesktopWindow;
  contextMenu: BfxBaseBlotterContextMenu<ConcreteEncodable>;
};

/* Example

@Component({
    selector: 'bfx-base-blotter-context-menu',
    templateUrl: './bfx-base-blotter-contextmenu.html',
    animations: [
        trigger('showHideMenu', [
            state('show', style({ opacity: 1 })),
            state('hide', style({ opacity: 0 })),
            transition('show => hide', animate('100ms ease-in')),
            transition('hide => show', animate('100ms ease-out')),
        ])
    ],
    styleUrls: ['./bfx-base-blotter-contextmenu.less']
})
*/

/**
 * Override this
 * The only thing that needs to be overriden here is the menuService provided,
 * in the sub class would need to be a specifc BlotterContextMenuService not the base one
 * See examples in the blotter and quotes overrides
 */
@Directive({
  providers: [
    {
      provide: BfxBaseBlotterContextMenu.LOOKUP_SERVICE,
      useValue: () => null,
      multi: true,
    },
  ],
  standalone: false,
})
export abstract class BfxBaseBlotterContextMenu<T extends ConcreteEncodable>
  implements AfterViewInit, OnDestroy
{
  private static globalShowHideSubject: Subject<BfxLadderBlotterContextMenuEvent<any>> =
    new Subject();

  private static referenceMap: NullProtoObject<RefEntry> = Object.create(null);

  public static instanceCount = 0;

  public id: string = `${++BfxBaseBlotterContextMenu.instanceCount}`;

  public position: any = { x: 0, y: 0 };
  public show: boolean = false;
  protected _menuItems: Array<BfxBlotterContextMenuItem<T>> = [];

  /** take snapshot of items in comparable state as items may be updated in place */
  protected menuItemsSnapshot: Array<{
    actionId: BfxBlotterAction;
    isAvailable: boolean;
    isEnabled: boolean;
  }> = [];

  protected enabledActionIds: Array<BfxBlotterAction> = [];
  protected actionDecorators: any = {};

  private _window: NativeDesktopWindow;

  private _windowResizeObserver: ResizeObserverScheduler;

  /** Used when resizing to recalculate the window origin */
  private windowOrigin: PointTopLeft = { left: 0, top: 0 };

  private _windowWidth: number = null;
  private _windowHeight: number = null;

  private parentElement: Element;

  private listeners: any = {};
  private menuId: string;

  private _currentLadderBlotter: any;

  public get currentLadderBlotter(): any {
    return this._currentLadderBlotter;
  }

  @Input()
  public set currentLadderBlotter(value: any) {
    if (this._currentLadderBlotter == null) {
      this._currentLadderBlotter = value;
    } else {
      logger.out.warn(
        `${this.constructor.name}.currentLadderBlotter (template?) assignment ignored`,
      );
    }
  }

  private modelSubscriptions = new Set<Subscription>();

  private prependHandlerActive: boolean = false;

  /**
   * only exists for multi: true
   * ignore / don't export / use
   */
  static LOOKUP_SERVICE = new InjectionToken<
    readonly PrimaryBlotterLookupService<string, ConcreteEncodable>[]
  >("context_menu_lookup_service");

  private updatedModelItems = new Map<string, object | typeof DELETED_SELECTED_ITEM>();
  private requestUpdateMenuSubject = new Subject<void>();
  private requestUpdateMenuSubscription: Subscription;
  private eventPosition: { x: number; y: number } = { x: -1, y: -1 };

  protected showMenuSubscription: Subscription;

  private globalHideSubscription: Subscription;

  protected menuService: BfxBaseBlotterContextMenuService<T>;

  constructor(
    protected injector: Injector,
    @Inject(BfxBaseBlotterContextMenu.LOOKUP_SERVICE)
    protected lookupServices:
      | PrimaryBlotterLookupService<string, T>
      | PrimaryBlotterLookupService<string, T>[],
    protected elementRef: ElementRef,
  ) {
    const menuService = injector.get<BfxBaseBlotterContextMenuService<T>>(
      this.getMenuServiceType(),
    );

    this.menuService = menuService;

    this.showMenuSubscription = menuService.showMenuSubject.subscribe(
      this.handleShowHideEvent.bind(this),
    );

    this.subscribeModel();
  }

  protected abstract getMenuServiceType(): Type<BfxBaseBlotterContextMenuService<T>>;

  private subscribeModel() {
    this.unsubscribeModel();
    const lookupServices:
      | PrimaryBlotterLookupService<string, T>
      | PrimaryBlotterLookupService<string, T>[] = this.lookupServices;

    if (Array.isArray(lookupServices)) {
      lookupServices.forEach((lookupService) => this.addModelUpdateSource(lookupService));
    } else if (lookupServices != null) {
      this.addModelUpdateSource(lookupServices);
    }

    this.requestUpdateMenuSubscription = this.requestUpdateMenuSubject
      .pipe(
        filter(() => this.updatedModelItems.size > 0),
        auditTime(500),
      )
      .subscribe({
        next: () => {
          const updatedModelItems = this.updatedModelItems;
          this.updatedModelItems = new Map();

          this.menuService.updateMenu(this.currentLadderBlotter, updatedModelItems);
        },
      });
  }

  private unsubscribeModel() {
    this.modelSubscriptions.forEach((modelSubscription) => {
      if (modelSubscription != null) {
        modelSubscription.unsubscribe();
      }
    });
    this.modelSubscriptions.clear();
    if (this.requestUpdateMenuSubscription != null) {
      this.requestUpdateMenuSubscription.unsubscribe();
      this.requestUpdateMenuSubscription = null;
    }
  }

  addModelUpdateSource(lookupService: PrimaryBlotterLookupService<string, T>): void {
    let updateSubscription = lookupService.getModelUpdateSubject().subscribe({
      next: (item) => {
        let id = lookupService.getBase().getItemKey(item);
        if (id != null) {
          this.updatedModelItems.set(id, item);
          this.requestUpdateMenuSubject.next();
        }
      },
    });

    let deleteSubscription = lookupService.getModelDeleteSubject().subscribe({
      next: (id) => {
        this.updatedModelItems.set(id, DELETED_SELECTED_ITEM);
        this.requestUpdateMenuSubject.next();
      },
    });

    this.modelSubscriptions.add(updateSubscription);
    this.modelSubscriptions.add(deleteSubscription);
  }

  protected initialize(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.menuId = this.createMenuId();

      NativeDesktopSupport.openWindow(
        "menu-" + this.menuId,
        WindowType.BlotterContextMenu,
        false,
        null,
        this.prepareDesktopMenu.bind(this),
      );
      this.initListeners();
    } else {
      // Publish a global hide event to hide all ladder blotter context menues
      // except this one
      this.menuService.showMenuSubject.subscribe((event) => {
        if (
          BfxLadderBlotterContextMenuEventType.MENU_SHOW === event.eventType &&
          event.ladderBlotter == this.currentLadderBlotter
        ) {
          let event = new BfxLadderBlotterContextMenuEvent(
            BfxLadderBlotterContextMenuEventType.MENU_HIDE_ALL,
            this.currentLadderBlotter,
          );
          BfxBaseBlotterContextMenu.globalShowHideSubject.next(event);
        }
      });

      this.globalHideSubscription = BfxBaseBlotterContextMenu.globalShowHideSubject.subscribe(
        (event) => {
          if (
            event.eventType === BfxLadderBlotterContextMenuEventType.MENU_HIDE_ALL &&
            event.ladderBlotter != this.currentLadderBlotter &&
            this.show == true
          ) {
            this.hideMenu();
          }
        },
      );
    }
  }

  protected prepareDesktopMenu(window: NativeDesktopWindow): void {
    this._window = window;
    BfxBaseBlotterContextMenu.referenceMap[this.menuId] = { window: window, contextMenu: this };
    this._window.attachToParentElement(this.elementRef.nativeElement.parentElement);

    this._windowResizeObserver = new ResizeObserverScheduler(() => {
      return new Promise((res) => this.checkResize(res));
    }, this.constructor.name);

    if (this.parentElement == null) {
      this.parentElement = this.elementRef.nativeElement.parentElement;
      this.position.x = this.position.y = 0;
    }

    if (this.parentElement != null) {
      let body = this._window.getNativeWindow().document.body;
      while (body.firstChild) {
        body.removeChild(body.lastChild);
      }
      body.appendChild(this.elementRef.nativeElement);
    }
  }

  ngAfterViewInit(): void {
    this.initialize();
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.

    if (this.showMenuSubscription) {
      this.showMenuSubscription.unsubscribe();
      this.showMenuSubscription = null;
    }
    this.unsubscribeModel();
    if (this._windowResizeObserver) {
      this._windowResizeObserver.complete();
      this._windowResizeObserver = null;
    }
    if (this._window) {
      this.removeListeners(this._window);
      delete BfxBaseBlotterContextMenu.referenceMap[this.menuId];
      this._window.close();
      this._window = null;
    }
    if (this.globalHideSubscription) {
      this.globalHideSubscription.unsubscribe();
      this.globalHideSubscription = null;
    }
  }

  public get menuItems(): Array<BfxBlotterContextMenuItem<T>> {
    return this._menuItems;
  }

  public set menuItems(value: Array<BfxBlotterContextMenuItem<T>>) {
    this._menuItems = value;

    this.menuItemsSnapshot = value.map((v) => {
      return {
        actionId: v.actionId,
        isAvailable: v.isAvailable,
        isEnabled: v.isEnabled,
      };
    });
  }

  public showMenuAt(x: number, y: number): void {
    // these are only hints as size not yet known
    //this.show = true;

    this.windowOrigin = { left: x, top: y };

    // code below added in order to trigger showHideMenuStarted() -> solves OMS-26424
    if (this.show && !NativeDesktopSupport.isNativeDesktopContainer()) {
      this.hideMenu();
    }

    if (this._window) {
      this.makeMenuVisible();
      this.showDesktop(x, y).then(() => {
        this.subscribeModel();
        this.eventPosition = { x: x, y: y };
      });
    } else {
      this.position.x = x;
      this.position.y = y;
      this.makeMenuVisible();
      this.subscribeModel();
      this.eventPosition = { x: x, y: y };
    }
  }

  private makeMenuVisible(): void {
    setTimeout(() => {
      this.show = true;
    }, 0);
  }

  private initListeners(): void {
    this.listeners["keyup"] = this.handleKeyboardEvent.bind(this);
    this.listeners["click"] = this.handleMouseClick.bind(this);
    this.listeners["mousewheel"] = this.handleMouseWheelEvent.bind(this);
  }

  private registerListeners(ofw: Window): void {
    if (ofw != null && ofw !== window) {
      let win: Window = ofw;

      if (win != null && win.document != null) {
        win.document.addEventListener("keyup", this.listeners["keyup"]);
        win.addEventListener("click", this.listeners["click"]);
        win.addEventListener("mousewheel", this.listeners["mousewheel"]);
      } else {
        logger.out.error(
          `${this.constructor.name}.registerListeners() failed to attach keyup, click and mousewheel listeners`,
        );
      }
    } else if (ofw === window) {
      // no registration needed? no need to print failure message
    } else {
      logger.out.error(
        `${this.constructor.name}.registerListeners() failed to attach keyup, click and mousewheel listeners`,
      );
    }
  }

  private removeListeners(ofw: NativeDesktopWindow): void {
    if (ofw != null) {
      let win: Window = ofw.getNativeWindow();

      if (win != null && win.document != null) {
        win.document.removeEventListener("keyup", this.listeners["keyup"]);
        win.removeEventListener("click", this.listeners["click"]);
        win.removeEventListener("mousewheel", this.listeners["mousewheel"]);
      } else {
        logger.out.error(
          `${this.constructor.name}.removeListeners() failed to remove keyup, click and mousewheel listeners`,
        );
      }
    } else {
      logger.out.error(
        `${this.constructor.name}.removeListeners() failed to remove keyup, click and mousewheel listeners`,
      );
    }
  }

  private async showDesktop(x: number, y: number): Promise<void> {
    let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.parentElement);
    let parentWindow: Window = this.parentElement.ownerDocument.defaultView;
    // TODO ELECTRON
    // let windowRef = fin.Window.wrapSync({ uuid: wnd.uuid, name: wnd.name });
    // if (!this.prependHandlerActive) {
    //   // Only add a single handler - set flag to indicate handler is active
    //   this.prependHandlerActive = true;
    //   windowRef["prependOnceListener"]("bounds-changed", () => {
    //     // Handler is now removed so mark as inactive
    //     this.prependHandlerActive = false;
    //     this.hideMenu();
    //   });
    // }

    await this.closeAll();

    this.registerListeners(parentWindow);

    let { width, height }: { width: number; height: number } = await this.resizeTo();

    this.position.x = this.position.y = 0;

    this.elementRef.nativeElement.firstElementChild["style"].opacity = "0";
    this.elementRef.nativeElement.firstElementChild["style"].visibility = "visible";
    this.elementRef.nativeElement.firstElementChild["style"].display = "block";

    x = x * NativeDesktopSupport.zoomLevel;
    y = y * NativeDesktopSupport.zoomLevel;

    let { x: newX, y: newY } = await NativeDesktopSupport.getUtilityWindowLocation(
      x + parentWindow.screenX,
      y + parentWindow.screenY,
      width,
      height,
    );

    this._window["visible"] = true;

    return new Promise<void>((res) => {
      this._window.showAt(newX, newY).then(
        async () => {
          this.elementRef.nativeElement.firstElementChild["style"].opacity = "1";
          this.elementRef.nativeElement.firstElementChild["style"].top = "0px";
          await NativeDesktopSupport.restoreUtility(this._window);
          requestAnimationFrame(() => {
            this._windowResizeObserver.observe(this.elementRef.nativeElement.firstElementChild);
            res();
          });
        },
        (err) => {
          logger.out.warn(`${this.constructor.name}.showDesktop() failed to showAt: ${err}`);
          res();
        },
      );
    });
  }

  private async closeAll(): Promise<void[]> {
    return Promise.all(
      Object.entries(BfxBaseBlotterContextMenu.referenceMap).map(
        ([_, { contextMenu, window }]) =>
          new Promise<void>((res) => {
            contextMenu.hideMenu();
            window
              .hide()
              .then(() => res())
              .catch((err) => {
                logger.out.warn(
                  `${this.constructor.name}.closeAll() failed to hide other windows: ${err}`,
                );
                res();
              });
          }),
      ),
    );
  }

  private async resizeTo(): Promise<{ width: number; height: number }> {
    let width: number =
      this.elementRef.nativeElement.firstElementChild["offsetWidth"] *
      NativeDesktopSupport.zoomLevel;
    let height: number =
      this.elementRef.nativeElement.firstElementChild["offsetHeight"] *
      NativeDesktopSupport.zoomLevel;

    if (width > 0 && height > 0) {
      return new Promise((res) => {
        this._window
          .setSize(width, height)
          .then(() => {
            res({ width, height });
          })
          .catch((err) => {
            logger.out.warn(
              `${this.constructor.name}.resizeTo() width:${width} height:${height} error:${err}`,
            );
            res({ width, height });
          });
      });
    }

    return Promise.resolve({ width, height });
  }

  private checkResize(res: () => void): void {
    const element = this.elementRef?.nativeElement;
    let width: number = element.firstElementChild["offsetWidth"] * NativeDesktopSupport.zoomLevel;
    let height: number = element.firstElementChild["offsetHeight"] * NativeDesktopSupport.zoomLevel;

    if (this._window == null) {
      logger.out.error(`${this.constructor.name}.checkResize() failed due to null _window`);
      return res();
    } else if (this._windowWidth === width && this._windowHeight === height) {
      return res();
    }

    if (width > 0 && height > 0) {
      let wnd = NativeDesktopSupport.getWindow(this.parentElement);
      let parentWindow: Window = wnd.getNativeWindow();
      let x = this.windowOrigin.left * NativeDesktopSupport.zoomLevel;
      let y = this.windowOrigin.top * NativeDesktopSupport.zoomLevel;

      this._windowWidth = width;
      this._windowHeight = height;

      NativeDesktopSupport.getUtilityWindowLocation(
        x + parentWindow.screenX,
        y + parentWindow.screenY,
        width,
        height,
      ).then(({ x: newX, y: newY }) => {
        this._window
          .setBounds(newX, newY, width, height)
          .then(() => {
            res();
          })
          .catch((err?) => {
            logger.out.warn(
              `${this.constructor.name}.checkResize() Failed to run setBounds() ${err}`,
            );
            res();
          });
      });
    } else {
      width = 171 * NativeDesktopSupport.zoomLevel;
      height = 400 * NativeDesktopSupport.zoomLevel;
      this._windowWidth = width;
      this._windowHeight = height;

      this._window
        .setSize(width, height)
        .then(() => {
          res();
        })
        .catch((err?) => {
          logger.out.warn(`${this.constructor.name}: Failed to run resizeTo() ${err}`);
          res();
        });
    }
  }
  public hideMenu(): boolean {
    this.unsubscribeModel();
    this.eventPosition = { x: -1, y: -1 };
    this.show = false;

    if (this._window) {
      this.removeListeners(this._window);
      this._window.hide();
      this._window["visible"] = false;
    }
    return false;
  }

  public selectedMenuItem(item: BfxBlotterContextMenuItem<T>, event: any) {
    if (this.isItemEnabled(item)) {
      this.menuService.fireActionEvent(
        item.actionId,
        this.currentLadderBlotter,
        null,
        null,
        null,
        null,
        null,
        event,
      );
      if (item.clearSelection) {
        this.currentLadderBlotter?.gridOptions?.api?.deselectAll();
        this.currentLadderBlotter?.onSelectionChanged([]);
      }
      this.hideMenu();
    }
  }

  // OMS-20923 - https://www.bcdfx.com/ijira/browse/OMS-20923
  // @HostListener('document:mouseup', ['$event'])
  // public handleMouseEvent(event: MouseEvent): void {
  //     if (this.show) {
  //         this.hideMenu();
  //     }
  // }

  @HostListener("window:click", ["$event"])
  public handleMouseClick(event: MouseEvent): void {
    let menuTarget = this.elementRef.nativeElement.contains(event.target);
    if (!menuTarget) {
      this.hideMenu();
    }
  }

  @HostListener("window:mousewheel", ["$event"])
  public handleMouseWheelEvent(event: MouseEvent): void {
    // Allow mousewheel events to operate on the context menu (as it can have a scrollbar) but hide if we're scrolling the
    // underlying blotter
    let menuTarget = this.elementRef.nativeElement?.contains(event.target);
    if (!menuTarget) {
      this.hideMenu();
    }
  }

  @HostListener("document:keyup", ["$event"])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.show) {
      this.hideMenu();
    }
  }

  public isItemEnabled(item: BfxBlotterContextMenuItem<T>) {
    return this.enabledActionIds.indexOf(item.actionId) >= 0;
  }

  public getActionDecorator(item: BfxBlotterContextMenuItem<T>) {
    return this.actionDecorators ? this.actionDecorators[item.actionId] : null;
  }

  public isItemSeparator(item: BfxBlotterContextMenuItem<T>) {
    return item.actionId === BfxBlotterAction.SEPARATOR;
  }

  protected isHideEvent(event: BfxLadderBlotterContextMenuEvent<T>): boolean {
    return BfxLadderBlotterContextMenuEventType.MENU_HIDE === event.eventType;
  }

  /**
   * this should return false for updates with equivalent
   *
   *  1. menuItems available/enabled
   *  2. actionIds
   *
   * @param event
   * @returns
   */
  protected isShowEvent(event: BfxLadderBlotterContextMenuEvent<T>): boolean {
    if (BfxLadderBlotterContextMenuEventType.MENU_SHOW !== event.eventType) {
      return false;
      if (!(event.x === this.eventPosition.x && event.y === this.eventPosition.y)) {
        return true;
      }
    }

    if (
      Array.isArray(event.menuItems) &&
      Array.isArray(this.menuItems) &&
      event.menuItems.length === this.menuItems.length
    ) {
      let diff = event.menuItems.some((menuItem, index) => {
        if (menuItem != null) {
          const existing = this.menuItemsSnapshot[index];
          return (
            existing == null ||
            existing.actionId !== menuItem.actionId ||
            existing.isAvailable !== menuItem.isAvailable ||
            existing.isEnabled !== menuItem.isEnabled
          );
        }
      });

      if (diff) {
        return true;
      }
    } else {
      return true;
    }

    if (
      Array.isArray(event.enabledActionIds) &&
      Array.isArray(this.enabledActionIds) &&
      event.enabledActionIds.length === this.enabledActionIds.length
    ) {
      let diff = event.enabledActionIds.some(
        (actionId, index) => this.enabledActionIds[index] !== actionId,
      );

      if (diff) {
        return true;
      }
    } else {
      return true;
    }

    return false;
  }

  protected handleShowHideEvent(event: BfxLadderBlotterContextMenuEvent<T>) {
    const templateError = this.currentLadderBlotter !== event.ladderBlotter;

    if (templateError) {
      logger.out.warn(`${this.constructor.name}.handleShowHideEvent() incorrect blotter specified`);
      return;
    }

    if (this.isHideEvent(event)) {
      this.menuItems = [];
      if (this.actionDecorators !== null) {
        this.actionDecorators = {};
      }
      this.enabledActionIds = [];
      this.hideMenu();
      return;
    }

    event.menuItems = this.processCandidateMenuItems(event.menuItems);
    const isShow = this.isShowEvent(event);
    if (isShow && event.menuItems.length > 0) {
      this.menuItems = event.menuItems;
      this.actionDecorators = event.actionDecorators;
      this.enabledActionIds = event.enabledActionIds;
      return this.handleShow(event);
    } else if (isShow && event.menuItems.length === 0) {
      console.log(
        `${this.constructor.name}.handleShowHideEvent(%O) has no menu items. skipping show`,
        event,
      );
    }
  }

  /**
   * Allow subclasses to reduce the menu item list before processing.
   * @param menuItems
   * @returns
   */
  protected processCandidateMenuItems(
    menuItems: BfxBlotterContextMenuItem<T>[],
  ): BfxBlotterContextMenuItem<T>[] {
    return menuItems;
  }

  protected handleShow(event: BfxLadderBlotterContextMenuEvent<T>) {
    // If we're showing internal menu, it'll be relative to parent container
    const isDesktop = NativeDesktopSupport.isNativeDesktopContainer();

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let blotterElement: Element = this.currentLadderBlotter?.BfxBlotterElement?.nativeElement;
      if (blotterElement != null) {
        let parentWnd = NativeDesktopSupport.getWindow(blotterElement);
        if (parentWnd != null) {
          this._window.setParentWindow(parentWnd);
        }
      }
    }

    let xLocn, yLocn;
    if (isDesktop) {
      xLocn = event.x;
      yLocn = event.y;
    } else {
      const viewElement = this.elementRef?.nativeElement;
      const containerOffsets = BFXDomHelper.getParentContainerOffsets(viewElement);
      xLocn = event.x - containerOffsets.left;
      yLocn = event.y - containerOffsets.top;
    }
    this.showMenuAt(xLocn, yLocn);
  }

  public showHideMenuStarted($event: AnimationEvent, menuRef: any) {
    if (`show` === $event.toState) {
      const isDesktop = NativeDesktopSupport.isNativeDesktopContainer();
      const offsets = BFXDomHelper.getParentContainerOffsets(menuRef);
      let height: number = menuRef.offsetHeight;
      let width: number = menuRef.offsetWidth;

      const containerWidth =
        offsets.containerWidth > 0 ? offsets.containerWidth : window.innerWidth;
      const containerHeight =
        offsets.containerHeight > 0 ? offsets.containerHeight : window.innerHeight;

      let maxHeight: number = containerHeight;
      let maxWidth: number = containerWidth;

      // For browser, we constrain top to inside the viewport
      const minY = isDesktop ? Number.MIN_SAFE_INTEGER : 0;
      if (height + this.position.y > maxHeight) {
        this.position.y = Math.max(maxHeight - height, minY);
      }

      if (width + this.position.x > maxWidth) {
        this.position.x = maxWidth - width;
      }
    }
  }

  protected createMenuId(): string {
    let menuId: string = null;

    if (this.currentLadderBlotter != null && this.currentLadderBlotter.blotterMenuId != null) {
      menuId = this.currentLadderBlotter.blotterMenuId + "_" + this.constructor.name;
    } else {
      menuId =
        (this.currentLadderBlotter ? this.currentLadderBlotter.blotterId + "_" : "") +
        this.constructor.name;
    }

    return menuId;
  }
}
